import React, { useState, useEffect, useCallback } from "react";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import { Dropdown, useToast } from "@swordsweeper/ssi-ui-component-library";
import { listLobas, selectLoba, validateOAuthCode } from "@src/api/CovetrusApi";
import { extractServerErrorMessage } from "@src/utils/error";
import FormContainer from "@src/components/containers/FormContainer";
import * as Yup from "yup";
import styles from "./CovetrusConnectionModalContent.module.scss";
import {
  CovetrusLobaConnectionPhases,
  type CovetrusLobaConnectionPhase,
} from "./CovetrusConnectionConstants";

export interface CovetrusLoba {
  uid: string;
  name: string;
  acbUid?: string;
  acbName?: string;
}

interface CovetrusLobaFormData {
  selectedLobaUid: string;
}

interface CovetrusLobaFormProps {
  formData: CovetrusLobaFormData;
  onChange: (data: { name: string; value: string }) => void;
  lobas?: CovetrusLoba[];
  showValidation?: boolean;
  [key: string]: unknown;
}

const CovetrusLobaFormSchema = Yup.object({
  selectedLobaUid: Yup.string()
    .trim()
    .required("Please select an account to connect."),
});

export type { CovetrusLobaConnectionPhase };

function formatLobaLabel(loba: CovetrusLoba): string {
  return `${loba.name}${loba.acbName ? ` - ${loba.acbName}` : ""}`;
}

function CovetrusLobaForm({
  formData,
  onChange,
  lobas = [],
  showValidation = false,
}: CovetrusLobaFormProps): React.JSX.Element {
  const selectedLoba = lobas.find((l) => l.uid === formData.selectedLobaUid);
  const showAccountError = showValidation && !formData.selectedLobaUid?.trim();

  return (
    <div className={styles.lobaForm}>
      <label id="covetrus-loba-select-label" className="text-sm">
        Select your account to connect:
      </label>
      <Dropdown
        aria-labelledby="covetrus-loba-select-label"
        items={lobas.map((loba) => ({
          value: loba.uid,
          label: formatLobaLabel(loba),
        }))}
        onItemClick={(item: { value: string }) => {
          onChange({ name: "selectedLobaUid", value: item.value });
        }}
        selectedLabel={
          selectedLoba ? formatLobaLabel(selectedLoba) : "Select an account"
        }
        customWidth="100%"
        customHeight="40px"
        dropdownIcon={<i className="fa-sharp fa-regular fa-chevron-down" />}
      />
      {showAccountError && (
        <p className="text-error text-sm" role="alert">
          Please select an account to connect.
        </p>
      )}
    </div>
  );
}

export function useCovetrusLobaConnection(
  oauthCode: string,
  oauthState?: string | null,
) {
  const { showToast } = useToast();
  const [phase, setPhase] = useState<CovetrusLobaConnectionPhase>(
    CovetrusLobaConnectionPhases.Loading,
  );
  const [lobas, setLobas] = useState<CovetrusLoba[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSavingLoba, setIsSavingLoba] = useState(false);

  const loadLobas = useCallback(async () => {
    try {
      const response = await listLobas();
      setLobas(response.body);
      if (response.body.length === 0) {
        setPhase(CovetrusLobaConnectionPhases.Error);
        setErrorMessage(
          "Connected to Covetrus, but no accounts were found. Please contact support.",
        );
        return;
      }
      setPhase(CovetrusLobaConnectionPhases.SelectAccount);
    } catch (err: unknown) {
      const errorObj = err as { response?: { body?: unknown } };
      const serverErrorMessage = extractServerErrorMessage(
        errorObj?.response?.body,
      );
      setPhase(CovetrusLobaConnectionPhases.Error);
      setErrorMessage(
        serverErrorMessage || "Unable to load Covetrus accounts.",
      );
    }
  }, []);

  useEffect(() => {
    if (!oauthCode) {
      return;
    }

    const exchangeCodeForTokens = async () => {
      setPhase(CovetrusLobaConnectionPhases.Loading);
      try {
        await validateOAuthCode(oauthCode, oauthState);
        await loadLobas();
      } catch (err: unknown) {
        console.error("Token exchange error:", err);
        const errorObj = err as { response?: { body?: unknown } };
        const serverErrorMessage = extractServerErrorMessage(
          errorObj?.response?.body,
        );
        showToast({
          message: serverErrorMessage,
          variant: "error",
        });
        setPhase(CovetrusLobaConnectionPhases.Error);
        setErrorMessage(serverErrorMessage);
      }
    };

    exchangeCodeForTokens();
  }, [oauthCode, oauthState, loadLobas, showToast]);

  const handleConnectSubmit = async (formData: CovetrusLobaFormData) => {
    const selectedUid = formData.selectedLobaUid?.trim();
    const loba = lobas.find((l) => l.uid === selectedUid);
    if (!selectedUid || !loba) {
      showToast({
        message: "Please select an account to complete the connection.",
        variant: "error",
      });
      return false;
    }

    setIsSavingLoba(true);
    try {
      await selectLoba({
        lobaUid: loba.uid,
        acbUid: loba.acbUid,
        lobaName: loba.name,
        acbName: loba.acbName,
      });
      return true;
    } catch (err: unknown) {
      const errorObj = err as { response?: { body?: unknown } };
      const serverErrorMessage = extractServerErrorMessage(
        errorObj?.response?.body,
      );
      showToast({
        message: serverErrorMessage || "Failed to save account selection",
        variant: "error",
      });
      return false;
    } finally {
      setIsSavingLoba(false);
    }
  };

  return {
    phase,
    lobas,
    errorMessage,
    isSavingLoba,
    handleConnectSubmit,
  };
}

interface CovetrusLobaModalBodyProps {
  phase: CovetrusLobaConnectionPhase;
  lobas: CovetrusLoba[];
  errorMessage: string | null;
  isSavingLoba: boolean;
  onClose: () => void;
  onSubmit: (formData: CovetrusLobaFormData) => void | Promise<void>;
}

export function CovetrusLobaModalBody({
  phase,
  lobas,
  errorMessage,
  isSavingLoba,
  onClose,
  onSubmit,
}: CovetrusLobaModalBodyProps): React.JSX.Element {
  if (isSavingLoba) {
    return <SpinningLoader />;
  }

  if (phase === CovetrusLobaConnectionPhases.Error) {
    return (
      <>
        <p className="text-error text-sm">{errorMessage}</p>
        <div className="margin-top-md">
          <button
            type="button"
            className="btn btn-primary btn-bordered btn-md"
            onClick={onClose}
            data-testid="form-cancel-button"
          >
            Close
          </button>
        </div>
      </>
    );
  }

  return (
    <CovetrusLobaFormContainer
      lobas={lobas}
      onCancel={onClose}
      onSubmit={onSubmit}
    />
  );
}

interface CovetrusLobaFormContainerProps {
  lobas: CovetrusLoba[];
  onCancel: () => void;
  onSubmit: (formData: CovetrusLobaFormData) => void | Promise<void>;
}

function CovetrusLobaFormContainer({
  lobas,
  onCancel,
  onSubmit,
}: CovetrusLobaFormContainerProps): React.JSX.Element {
  const [showValidation, setShowValidation] = React.useState(false);

  const handleSubmit = (formData: CovetrusLobaFormData) => {
    setShowValidation(true);
    return onSubmit(formData);
  };

  return (
    <FormContainer
      form={CovetrusLobaForm}
      schema={CovetrusLobaFormSchema}
      initialData={{ selectedLobaUid: "" }}
      additionalFormProps={{ lobas, showValidation }}
      onCancel={onCancel}
      onSubmit={handleSubmit}
      submitButtonText="Submit"
    />
  );
}

interface CovetrusLobaSelectionStepProps {
  oauthCode: string;
  oauthState?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function CovetrusLobaSelectionStep({
  oauthCode,
  oauthState,
  onClose,
  onSuccess,
}: CovetrusLobaSelectionStepProps): React.JSX.Element | null {
  const { phase, lobas, errorMessage, isSavingLoba, handleConnectSubmit } =
    useCovetrusLobaConnection(oauthCode, oauthState);

  if (phase === CovetrusLobaConnectionPhases.Loading) {
    return null;
  }

  const handleSubmit = async (formData: CovetrusLobaFormData) => {
    const success = await handleConnectSubmit(formData);
    if (success) {
      onSuccess();
    }
  };

  return (
    <CovetrusLobaModalBody
      phase={phase}
      lobas={lobas}
      errorMessage={errorMessage}
      isSavingLoba={isSavingLoba}
      onClose={onClose}
      onSubmit={handleSubmit}
    />
  );
}
